import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@repo/ui/src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import type { EnrollmentWithSubject } from '@repo/shared-types';
import { enrollmentDisplayName, filterByName, sortByGivenName } from '../lib/enrollmentSearch';

/**
 * Searchable "add student to this session" picker (makeup/join) — swaps the
 * bare `<select>` a ~80-student roster made unusable. Sorted by given name
 * (client-side mirror of the DB sort — this endpoint's list comes back
 * `createdAt desc` with no `givenName` column) and filtered
 * diacritic-insensitively so "thien an" matches "Thiên Ân" while typing
 * without dấu.
 *
 * Filtering is done ourselves (`shouldFilter={false}`) rather than via cmdk's
 * built-in fuzzy filter: cmdk matches raw characters against the item's
 * `value`, so a diacritic-stripped value would stop matching a search typed
 * *with* dấu — normalizing both sides the same way avoids that asymmetry.
 *
 * The filter also matches `student.nickname` (tên ở nhà) — often the only
 * name the teacher remembers — and the option label appends it in brackets
 * when set, same disambiguation pattern as `ClientCombobox`'s phone-in-brackets
 * (THI-82).
 *
 * The matching, the given-name ordering and the `name (nickname)` label all
 * live in `lib/enrollmentSearch.ts` so `EnrollmentMultiCombobox` shares them
 * rather than re-deriving them.
 */
export function AddStudentCombobox({
  options, onSelect, disabled, placeholder,
}: {
  options: EnrollmentWithSubject[];
  onSelect: (enrollmentId: string) => void;
  disabled?: boolean;
  placeholder: string;
}) {
  const { t } = useTranslation('scheduling');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Sorting is memoised separately from filtering on purpose: the order does
  // not change while the teacher types, so every keystroke re-filters an
  // already-sorted list rather than re-sorting one.
  const sorted = useMemo(() => sortByGivenName(options), [options]);
  const filtered = useMemo(() => filterByName(sorted, search), [sorted, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal min-h-[44px]"
        >
          <span className="truncate text-muted-foreground">{placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
        <Command shouldFilter={false} className="[&_[cmdk-item]]:min-h-[44px]">
          <CommandInput
            placeholder={t('session.addSearchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-64">
            <CommandEmpty>{t('session.addEmpty')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((e) => (
                <CommandItem
                  key={e.id}
                  value={e.id}
                  onSelect={() => {
                    onSelect(e.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  {enrollmentDisplayName(e)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
